import type { FragmentType, Id } from '@e-socna/data-layer';

import { FrozenServiceUsagesGroups } from '../../components/ServiceUsages/FrozenGroups';
import type { FrozenServiceUsagesGroup } from '../../components/ServiceUsages/FrozenGroups';
import type { ServiceUsagesTotalCardCmp_GroupTotalCalculation_Fragment } from '../../components/ServiceUsages/TotalCard';
import { buildGroupTotalCalculationMap } from '../../utils/build-group-total-calculation-map.util';

import { SubMissionPricingCards } from './components/SubMissionPricingCards';
import { buildSubMissionTotalCalculation } from './utils/sub-mission-total.util';
import type { SubMissionPricingCardItem } from './components/SubMissionPricingCards';
import type { SubMissionPricing } from './types/sub-mission-pricing.type';
import type { SubMissionSnapshotSection } from './utils/split-snapshot-groups-by-sub-mission.util';

interface Props {
  readonly groupTotalCalculations:
    | readonly FragmentType<
        typeof ServiceUsagesTotalCardCmp_GroupTotalCalculation_Fragment
      >[]
    | null;
  readonly sections: readonly SubMissionSnapshotSection<FrozenServiceUsagesGroup>[];
  readonly selectedOptionGroupIds: readonly Id[] | null | undefined;
  readonly subMissions: readonly SubMissionPricing[];
}

// Frozen by the quotation, a collaboration shows the groups of its quotation
// snapshot, still one card per sub-mission.
export const FrozenSubMissionsPricing = ({
  groupTotalCalculations,
  sections,
  selectedOptionGroupIds,
  subMissions,
}: Readonly<Props>) => {
  const groupTotalCalculationMap = buildGroupTotalCalculationMap(
    groupTotalCalculations,
  );
  const subMissionsById = new Map<Id, SubMissionPricing>(
    subMissions.map((subMission) => [subMission._id, subMission]),
  );

  const items = sections.flatMap(
    ({ subMissionId, groups }): SubMissionPricingCardItem[] => {
      const subMission = subMissionsById.get(subMissionId);

      if (!subMission) {
        return [];
      }

      const totalCalculation = buildSubMissionTotalCalculation({
        groups,
        groupTotalCalculationMap,
        selectedOptionGroupIds,
      });

      return [
        {
          subMission,
          totalCalculation,
          detail: (
            <FrozenServiceUsagesGroups
              consultResolvableServiceInNewTab
              groupTotalCalculations={groupTotalCalculations}
              groups={groups}
              selectedOptionGroupIds={selectedOptionGroupIds}
              totalCalculation={totalCalculation}
            />
          ),
        },
      ];
    },
  );

  return <SubMissionPricingCards items={items} />;
};
